<script setup>
import XIcon from '@iconify-vue/ph/x';
import LoadingSpinner from '@js/components/LoadingSpinner.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import { AnimatePresence, Motion } from 'motion-v';
import {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    DialogTrigger,
    VisuallyHidden,
} from 'reka-ui';
import { provide } from 'vue';

const isOpen = defineModel({ required: false, default: false });

const { size = 'medium', loading = false } = defineProps({
    size: {
        type: String,
        required: false,
    },
    loading: {
        type: Boolean,
        required: false,
    },
});

provide('isInDialog', true);
provide('isInDialogModal', true);
</script>

<template>
    <DialogRoot v-model:open="isOpen">
        <DialogTrigger v-if="$slots.trigger" as-child>
            <slot name="trigger" />
        </DialogTrigger>

        <DialogPortal>
            <AnimatePresence>
                <Motion
                    v-if="isOpen"
                    :initial="{ opacity: 0 }"
                    :animate="{ opacity: 1 }"
                    :exit="{ opacity: 0 }"
                    :transition="{ duration: 0.2 }"
                    class="fixed inset-0 z-50"
                >
                    <DialogOverlay class="size-full bg-black/75" />
                </Motion>

                <Motion
                    v-if="isOpen"
                    :initial="{ opacity: 0, scale: 0.96 }"
                    :animate="{ opacity: 1, scale: 1 }"
                    :exit="{ opacity: 0, scale: 0.96 }"
                    :transition="{
                        type: 'spring',
                        duration: 0.3,
                        bounce: 0.1,
                    }"
                    class="fixed top-4 left-1/2 z-50 flex w-full -translate-x-1/2 items-start justify-center overflow-y-auto px-4"
                    :class="{
                        'max-w-full': size === 'almost-full',
                        'max-w-[100rem]': size === 'extra-large',
                        'max-w-[80rem]': size === 'large',
                        'max-w-[60rem]': size === 'medium',
                        'max-w-[40rem]': size === 'small',
                    }"
                >
                    <DialogContent
                        class="flex max-h-[calc(100vh-8*var(--spacing))] w-full flex-col overflow-hidden rounded-sm bg-gray-100 drop-shadow-sm"
                        :class="{
                            'max-w-full': size === 'almost-full',
                            'max-w-[100rem]': size === 'extra-large',
                            'max-w-[80rem]': size === 'large',
                            'max-w-[60rem]': size === 'medium',
                            'max-w-[40rem]': size === 'small',
                        }"
                        :aria-describedby="undefined"
                    >
                        <div
                            class="relative flex items-center justify-start border-b border-gray-200 bg-white p-6"
                        >
                            <VisuallyHidden>
                                <DialogTitle>
                                    <slot name="title" />
                                </DialogTitle>
                            </VisuallyHidden>

                            <div class="pr-6 font-medium">
                                <slot v-if="!loading" name="title" />
                            </div>

                            <div
                                class="absolute top-1/2 right-6 -translate-y-1/2"
                            >
                                <DialogClose
                                    :as="ActionButton"
                                    :icon="XIcon"
                                    square
                                    type="bordered"
                                    size="small"
                                />
                            </div>
                        </div>

                        <div
                            v-if="$slots['actions']"
                            class="flex justify-end gap-2 border-b border-gray-200 bg-white px-4 py-3"
                        >
                            <slot name="actions" />
                        </div>

                        <div v-if="loading" class="p-6">
                            <LoadingSpinner />
                        </div>

                        <template v-else>
                            <div
                                v-if="$slots['full-content']"
                                class="h-full overflow-y-auto"
                            >
                                <slot name="full-content" />
                            </div>

                            <div
                                v-if="$slots.content"
                                class="max-h-full overflow-y-auto p-6"
                            >
                                <slot name="content" />
                            </div>

                            <div
                                v-if="$slots.footer"
                                class="border-t border-gray-200 bg-white p-6"
                            >
                                <slot name="footer" />
                            </div>
                        </template>
                    </DialogContent>
                </Motion>
            </AnimatePresence>
        </DialogPortal>
    </DialogRoot>
</template>
